/**
 * useDigest — the React binding for the AI Team Digest (section 18a).
 *
 * Picks a reporting range, retrieves the entities active in that window
 * CLIENT-SIDE (via {@link useContextSnapshot}), formats compact context, and
 * calls REAL Bedrock (`converse`) to produce a sectioned digest. The entities
 * that fed the digest are returned as `usedRefs` for transparency + linking.
 */

import { useCallback, useMemo, useState } from 'react';
import { useContextSnapshot } from './useContextSnapshot';
import { formatContext, refsInRange, sortByRecency } from './contextRetriever';
import { buildSprintExecutionContext } from './sprintDigest';
import {
  SUMMARY_MAX_TOKENS,
  SUMMARY_TEMPERATURE,
  buildDigestPrompt,
  buildDigestSystemPrompt,
  formatKbSources,
  friendlyInferenceError,
  rangeLabel,
  windowForRange,
} from './intelligenceModel';
import type { DigestRange, DigestResult } from './types';

export interface UseDigestResult {
  range: DigestRange;
  setRange: (range: DigestRange) => void;
  result: DigestResult | null;
  loading: boolean;
  generating: boolean;
  error: string | null;
  /** Count of entities that fall in the current range (pre-generation hint). */
  inRangeCount: number;
  /** When true, augments the prompt with KB-retrieved passages. */
  useKb: boolean;
  setUseKb: (useKb: boolean) => void;
  generate: () => Promise<void>;
}

export function useDigest(): UseDigestResult {
  const { refs, loading, error, converse, kbRetrieve, snapshot } =
    useContextSnapshot();
  const [range, setRange] = useState<DigestRange>('weekly');
  const [result, setResult] = useState<DigestResult | null>(null);
  const [generating, setGenerating] = useState(false);
  const [useKb, setUseKb] = useState(false);

  const window = useMemo(() => windowForRange(range), [range]);
  const inRange = useMemo(
    () => sortByRecency(refsInRange(refs, window.fromIso, window.toIso)),
    [refs, window],
  );

  const generate = useCallback(async () => {
    setGenerating(true);
    const usedRefs = inRange;
    const context = formatContext(usedRefs);
    const sprintContext = snapshot
      ? buildSprintExecutionContext(
          snapshot.sprints,
          snapshot.tasks,
          snapshot.taskUpdates,
          window,
        )
      : '';
    let kbContext = '';
    if (useKb) {
      try {
        const sources = await kbRetrieve(
          `${rangeLabel(range)} team updates, sprint progress, scope changes, standup, decisions, and blockers`,
          8,
        );
        kbContext = formatKbSources(sources);
      } catch {
        /* KB unavailable — proceed with entity-only context */
      }
    }
    try {
      const markdown = await converse(
        buildDigestPrompt(context, window, kbContext, sprintContext),
        buildDigestSystemPrompt(),
        { maxTokens: SUMMARY_MAX_TOKENS, temperature: SUMMARY_TEMPERATURE },
      );
      setResult({
        range,
        window,
        markdown: markdown.trim() || '_(empty response)_',
        usedRefs,
      });
    } catch (e) {
      const raw = e instanceof Error ? e.message : 'Inference request failed.';
      setResult({ range, window, markdown: friendlyInferenceError(raw), usedRefs });
    } finally {
      setGenerating(false);
    }
  }, [inRange, window, range, converse, useKb, kbRetrieve, snapshot]);

  return {
    range,
    setRange,
    result,
    loading,
    generating,
    error,
    inRangeCount: inRange.length,
    useKb,
    setUseKb,
    generate,
  };
}
